/**
 * Device Service API Contracts
 * Request/Response schemas for device management (printers, scanners, gates, etc.)
 */

import { z } from 'zod';

// ============================================================================
// Device Type & Status Enums
// ============================================================================

export const DeviceTypeSchema = z.enum([
  'CAMERA',
  'CASHLESS_READER',
  'LED_DISPLAY',
  'MONITOR',
  // Ticket printers and wristband printers are the same physical hardware —
  // there is deliberately no separate TICKET_PRINTER type (2026-08-13).
  'WRISTBAND_PRINTER',
  'RECEIPT_PRINTER',
  'QR_SCANNER',
  'GATE',
  'GENERAL_DEVICE',
  'FACE_RECOGNITION',
  'TURNSTILE_IN',
  'TURNSTILE_OUT',
  'ATTRACTION_CAMERA_IN',
  'ATTRACTION_CAMERA_OUT',
  // Bank EDC card-payment terminals — one DeviceType per bank (2026-08-14,
  // afnabdillah PR #982), NOT a single generic EDC_TERMINAL; distinct from
  // CASHLESS_READER since a counter can hold both an e-money reader and a
  // card EDC at once. FE/BE match these by the `EDC_` prefix (see
  // EDC_TYPE_PREFIX below), not an explicit list, so adding another bank
  // later needs only a new enum value + migration. Starting with Permata
  // only — add more EDC_<BANK> values here as they're actually needed.
  'EDC_PERMATA',
]);

export type DeviceType = z.infer<typeof DeviceTypeSchema>;

/** Prefix shared by every bank-specific EDC device type (EDC_PERMATA, and
 * more EDC_<BANK> values to follow later) — FE/BE match on this prefix
 * instead of an explicit type list, so adding a bank needs only a new enum
 * value + migration, no code changes (afnabdillah, PR #982). */
export const EDC_TYPE_PREFIX = 'EDC_';

export function isEdcDeviceType(type: string): type is DeviceType {
  return type.startsWith(EDC_TYPE_PREFIX);
}

/**
 * Mount-post taxonomy for the device↔location binding. The typed values
 * (MAIN_GATE / ATTRACTION_GATE / ZONE_GATE) resolve 1:1 to ticketing-service
 * Attraction.kind (PARK / ATTRACTION / ZONE respectively) — `locationId` on
 * the device then points at an Attraction row of that kind. The free-text
 * values (KIOSK / OFFICE / TICKETBOX) have no backing row; `location` is a
 * plain human-readable description instead.
 */
export const DeviceLocationTypeSchema = z.enum([
  'MAIN_GATE',
  'ATTRACTION_GATE',
  'ZONE_GATE',
  'KIOSK',
  'OFFICE',
  'TICKETBOX',
]);

export type DeviceLocationType = z.infer<typeof DeviceLocationTypeSchema>;

/** Typed mount posts — require `locationId` (an Attraction row). */
export const TYPED_DEVICE_LOCATION_TYPES: readonly DeviceLocationType[] = [
  'MAIN_GATE',
  'ATTRACTION_GATE',
  'ZONE_GATE',
];

/** Free-text mount posts — require a `location` description, no `locationId`. */
export const FREE_TEXT_DEVICE_LOCATION_TYPES: readonly DeviceLocationType[] = [
  'KIOSK',
  'OFFICE',
  'TICKETBOX',
];

export const DeviceStatusSchema = z.enum([
  'ONLINE',
  'OFFLINE',
  'MAINTENANCE',
  'ERROR',
  'DECOMMISSIONED',
]);

export type DeviceStatus = z.infer<typeof DeviceStatusSchema>;

/**
 * Query for the POS device picker — `GET /device`.
 *
 * Both fields reached Prisma as an unchecked `as DeviceStatus` / `as DeviceType`
 * cast, so any value the client invented became
 * `PrismaClientKnownRequestError: Invalid value for argument 'status'` and the
 * request 500'd. `apps/handheld` sends `?status=ACTIVE` — a Mongo-era literal
 * that is not in the Postgres enum — so the device picker 500'd on every load
 * and the operator saw "NO POSTS FOUND".
 *
 * Omitted `status` is left undefined here rather than defaulted, because the
 * service's own default (ONLINE — working hardware only) is the meaningful one
 * and belongs with the query it guards.
 */
/**
 * `TICKET_PRINTER` is the monolith's name for a WRISTBAND_PRINTER — the enum above says
 * why there is no separate type ("the same physical hardware"). `apps/ticketbox` still
 * asks for it by that name (DeviceSetupPage.tsx: `useDeviceOptions('TICKET_PRINTER')`).
 */
const DEVICE_TYPE_ALIASES: Record<string, string> = {
  TICKET_PRINTER: 'WRISTBAND_PRINTER',
};

const DeviceTypeQuerySchema = z.preprocess(
  (v) => (typeof v === 'string' ? (DEVICE_TYPE_ALIASES[v] ?? v) : v),
  DeviceTypeSchema,
);

export const DeviceOptionsQuerySchema = z
  .object({
    type: DeviceTypeQuerySchema.optional(),
    /**
     * The monolith's spelling of `type`, which `apps/ticketbox` sends
     * (`getDevices()` builds `{ deviceType }`). An unknown query key is dropped rather
     * than rejected, so this did not fail — the filter simply never applied and the
     * picker returned EVERY online device: the till's "ticket printer" dropdown offered
     * a cashless reader, and binding one would have been accepted.
     */
    deviceType: DeviceTypeQuerySchema.optional(),
    status: DeviceStatusSchema.optional(),
  })
  // Collapse to the one name the service reads, so callers can use either spelling.
  // The return type is annotated rather than inferred: an inferred transform output makes
  // both keys REQUIRED (as `T | undefined`), which breaks every existing caller that
  // passes a partial query.
  .transform(
    ({ type, deviceType, status }): { type?: DeviceType; status?: DeviceStatus } => ({
      type: type ?? deviceType,
      status,
    }),
  );

export type DeviceOptionsQuery = z.infer<typeof DeviceOptionsQuerySchema>;

// ============================================================================
// Device Entity
// ============================================================================

export const DeviceSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: DeviceTypeSchema,
  primaryId: z.string().nullable().optional(),
  secondaryId: z.string().nullable().optional(),
  deviceModel: z.string().nullable().optional(),
  macAddress: z.string().nullable().optional(),
  serialNumber: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  // Device login password. Only exposed on the edit form — never rendered
  // in the read-only detail view.
  password: z.string().nullable().optional(),
  localIpAddress: z.string().nullable().optional(),
  publicIpAddress: z.string().nullable().optional(),
  localPort: z.number().int().nullable().optional(),
  publicPort: z.number().int().nullable().optional(),
  domain: z.string().nullable().optional(),
  localProtocol: z.string().nullable().optional(),
  publicProtocol: z.string().nullable().optional(),
  protocol: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  vendorId: z.string().nullable().optional(),
  productId: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  locationType: DeviceLocationTypeSchema.nullable().optional(),
  // Cross-service reference to ticketing-service's Attraction.id — the row
  // this device is physically mounted at. Only meaningful when locationType
  // is one of TYPED_DEVICE_LOCATION_TYPES. Distinct from `attractionId`
  // below, which is turnstile-specific (#716).
  locationId: z.string().nullable().optional(),
  // Cross-service reference to ticketing-service's Attraction.id. Only
  // meaningful for TURNSTILE_IN/TURNSTILE_OUT devices (#716).
  attractionId: z.string().nullable().optional(),
  status: DeviceStatusSchema,
  isActive: z.boolean(),
  lastPingAt: z.string().datetime().nullable().optional(),
  firmware: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Device = z.infer<typeof DeviceSchema>;

// ============================================================================
// Create Device Request
// ============================================================================

export const CreateDeviceRequestSchema = z.object({
  code: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  type: DeviceTypeSchema,
  primaryId: z.string().max(200).optional(),
  secondaryId: z.string().max(200).optional(),
  deviceModel: z.string().max(200).optional(),
  macAddress: z.string().max(50).optional(),
  serialNumber: z.string().max(200).optional(),
  username: z.string().max(200).optional(),
  password: z.string().max(200).optional(),
  localIpAddress: z.string().max(45).optional(),
  publicIpAddress: z.string().max(45).optional(),
  localPort: z.number().int().min(1).max(65535).optional(),
  publicPort: z.number().int().min(1).max(65535).optional(),
  domain: z.string().max(253).optional(),
  localProtocol: z.string().max(10).optional(),
  publicProtocol: z.string().max(10).optional(),
  protocol: z.string().max(10).optional(),
  path: z.string().max(500).optional(),
  vendorId: z.string().max(50).optional(),
  productId: z.string().max(50).optional(),
  location: z.string().max(200).optional(),
  locationType: DeviceLocationTypeSchema.optional(),
  locationId: z.string().max(200).optional(),
  attractionId: z.string().max(200).optional(),
  firmware: z.string().max(100).optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type CreateDeviceRequest = z.infer<typeof CreateDeviceRequestSchema>;

// ============================================================================
// Update Device Request
// ============================================================================

export const UpdateDeviceRequestSchema = CreateDeviceRequestSchema.partial().extend({
  status: DeviceStatusSchema.optional(),
  isActive: z.boolean().optional(),
  // Nullable (not just optional, overriding the inherited .partial() version):
  // unassigning a turnstile from its attraction needs to explicitly clear
  // attractionId back to unbound — omitting the field means "leave
  // unchanged", `null` means "clear it" (#716).
  attractionId: z.string().nullable().optional(),
  // Same nullable-override reasoning as attractionId above: switching a
  // device from a typed mount post to free-text needs to explicitly clear
  // locationId, not just leave it dangling (2026-08-13).
  locationId: z.string().nullable().optional(),
});

export type UpdateDeviceRequest = z.infer<typeof UpdateDeviceRequestSchema>;

// ============================================================================
// Device List
// ============================================================================

/**
 * GET /devices was previously unbounded (`findMany()` with no `take`) — the
 * devices page and every `useDevicesForSelect` slot-picker fetched the
 * entire inventory on every load. Capped at 100, matching the org-wide list
 * limit convention (identity-service's admin users list is also max 100).
 */
export const DeviceListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(100),
});

export type DeviceListQuery = z.infer<typeof DeviceListQuerySchema>;

export const DeviceListResponseSchema = z.array(DeviceSchema);

export type DeviceListResponse = z.infer<typeof DeviceListResponseSchema>;

// ============================================================================
// UserDevice — workstation bindings (#744 /user-devices)
// ============================================================================

export const UserDeviceTypeSchema = z.enum([
  'KIOSK',
  'TICKETBOX',
  'TICKET_VALIDATOR',
]);

export type UserDeviceType = z.infer<typeof UserDeviceTypeSchema>;

/** Safe device projection nested on UserDevice slot responses. */
export const UserDeviceSlotSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  type: DeviceTypeSchema,
  status: DeviceStatusSchema,
  location: z.string().nullable().optional(),
});

export type UserDeviceSlotSnapshot = z.infer<typeof UserDeviceSlotSnapshotSchema>;

const optionalSlotId = z.string().min(1).nullable().optional();

export const UserDeviceSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string().nullable(),
  type: UserDeviceTypeSchema.nullable(),
  // Permata EDC card-payment terminal (2026-08-13). A workstation can hold
  // more than one — array, not the scalar `*Id`/`*` pair every other slot
  // uses (afnabdillah, PR #982).
  edcTerminalIds: z.array(z.string()),
  edcTerminals: z.array(UserDeviceSlotSnapshotSchema),
  wristbandPrinterId: z.string().nullable(),
  wristbandPrinter: UserDeviceSlotSnapshotSchema.nullable().optional(),
  receiptPrinterId: z.string().nullable(),
  receiptPrinter: UserDeviceSlotSnapshotSchema.nullable().optional(),
  // TICKET_VALIDATOR's QR scanner (afnabdillah, PR #982).
  qrScannerId: z.string().nullable(),
  qrScanner: UserDeviceSlotSnapshotSchema.nullable().optional(),
  // boomgateId is intentionally NOT a UserDevice slot here — the main-gate
  // pedestrian scan flow (device-service/src/scan/scan.service.ts) reads it
  // via the separate legacy `POST users/:userId/boomgate` binding, not the
  // KIOSK/TICKETBOX/TICKET_VALIDATOR post model this schema covers.
  // #716 — separate from attractionId on the Device row
  turnstileInId: z.string().nullable(),
  turnstileIn: UserDeviceSlotSnapshotSchema.nullable().optional(),
  turnstileOutId: z.string().nullable(),
  turnstileOut: UserDeviceSlotSnapshotSchema.nullable().optional(),
  status: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type UserDevice = z.infer<typeof UserDeviceSchema>;

export const CreateUserDeviceRequestSchema = z.object({
  userId: z.string().min(1),
  name: z.string().min(1).max(200),
  type: UserDeviceTypeSchema,
  edcTerminalIds: z.array(z.string().min(1)).optional(),
  wristbandPrinterId: optionalSlotId,
  receiptPrinterId: optionalSlotId,
  qrScannerId: optionalSlotId,
  turnstileInId: optionalSlotId,
  turnstileOutId: optionalSlotId,
});

export type CreateUserDeviceRequest = z.infer<typeof CreateUserDeviceRequestSchema>;

/**
 * Partial patch. Omitted keys = leave alone; `null` = clear slot.
 * Optional `userId` ≠ path userId → transfer ownership.
 */
export const UpdateUserDeviceRequestSchema = z
  .object({
    userId: z.string().min(1).optional(),
    name: z.string().min(1).max(200).optional(),
    type: UserDeviceTypeSchema.optional(),
    // Omitted = leave unchanged; `[]` = clear all; anything else = full
    // replace of the set (not a diff/patch of individual entries).
    edcTerminalIds: z.array(z.string().min(1)).optional(),
    wristbandPrinterId: optionalSlotId,
    receiptPrinterId: optionalSlotId,
    qrScannerId: optionalSlotId,
    turnstileInId: optionalSlotId,
    turnstileOutId: optionalSlotId,
  })
  .refine(
    (body) =>
      body.userId !== undefined ||
      body.name !== undefined ||
      body.type !== undefined ||
      body.edcTerminalIds !== undefined ||
      body.wristbandPrinterId !== undefined ||
      body.receiptPrinterId !== undefined ||
      body.qrScannerId !== undefined ||
      body.turnstileInId !== undefined ||
      body.turnstileOutId !== undefined,
    { message: 'NO_SLOTS_SUPPLIED' },
  );

export type UpdateUserDeviceRequest = z.infer<typeof UpdateUserDeviceRequestSchema>;

export const UserDeviceListQuerySchema = z.object({
  type: UserDeviceTypeSchema.optional(),
  status: z.string().optional(),
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
});

export type UserDeviceListQuery = z.infer<typeof UserDeviceListQuerySchema>;

export const UserDeviceListResponseSchema = z.object({
  data: z.array(UserDeviceSchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
  }),
});

export type UserDeviceListResponse = z.infer<typeof UserDeviceListResponseSchema>;

/**
 * Slot key → allowed DeviceType(s). Shared by FE dropdowns and BE
 * validation. edcTerminalIds is NOT here — it's a multi-value field (a
 * workstation can hold more than one EDC terminal), handled separately from
 * these single-value `*Id` slots.
 */
export const USER_DEVICE_SLOT_TYPES = {
  wristbandPrinterId: ['WRISTBAND_PRINTER'],
  receiptPrinterId: ['RECEIPT_PRINTER'],
  // TICKET_VALIDATOR (afnabdillah, PR #982).
  qrScannerId: ['QR_SCANNER'],
  // #716 — separate turnstile slots
  turnstileInId: ['TURNSTILE_IN'],
  turnstileOutId: ['TURNSTILE_OUT'],
} as const satisfies Record<string, readonly DeviceType[]>;

export type UserDeviceSlot = keyof typeof USER_DEVICE_SLOT_TYPES;

export const USER_DEVICE_SLOTS = Object.keys(
  USER_DEVICE_SLOT_TYPES,
) as UserDeviceSlot[];


// ============================================================================
// Gate scan — device-service `POST /api/v1/qr-code/scan`
// ============================================================================
//
// The monolith sent `qr_code` and the gate app still does; `value` is accepted
// so an internal caller need not speak the legacy spelling. The handler used to
// coalesce them with `body?.qr_code ?? body?.value ?? ''` and hand the result
// to the scanner, so a request with neither field scanned the empty string —
// which is not a miss the scanner handles, but a 500.
//
// The refine is what the coalesce should have been: at least one spelling, and
// non-empty.

export const GateScanRequestSchema = z
  .object({
    qr_code: z.string().min(1).optional(),
    value: z.string().min(1).optional(),
  })
  .refine((b) => Boolean(b.qr_code ?? b.value), {
    message: 'one of qr_code or value is required',
    path: ['qr_code'],
  });
export type GateScanRequest = z.infer<typeof GateScanRequestSchema>;
